import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { DateTimeField } from '../../../../components/dateFields';
import Select from '../../../../components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../../../components/ui/Dialog';
import { generateBracket } from '@/api/TournamentManagement/tournamentApi';
import { MatchFormat, MatchType } from '@/models/tournament';
import type { GenerateBracketInput } from '@/models/tournament';

interface GenerateBracketModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: number;
  onGenerated?: () => void;
}

const GenerateBracketModal: React.FC<GenerateBracketModalProps> = ({
  isOpen,
  onClose,
  tournamentId,
  onGenerated,
}) => {
  const { t } = useTranslation('tournament');

  const [teamNames, setTeamNames] = useState<string[]>(['', '', '']);
  const [defaultLocation, setDefaultLocation] = useState('');
  const [firstRoundDateTime, setFirstRoundDateTime] = useState('');
  const [hoursBetweenMatches, setHoursBetweenMatches] = useState(2);
  const [daysBetweenRounds, setDaysBetweenRounds] = useState(7);
  const [format, setFormat] = useState<string>(String(MatchFormat.ElevenAside));
  const [type, setType] = useState<string>(String(MatchType.Outdoor));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const formatOptions = [
    { value: String(MatchFormat.FiveAside), label: t('5-a-side') },
    { value: String(MatchFormat.SevenAside), label: t('7-a-side') },
    { value: String(MatchFormat.NineAside), label: t('9-a-side') },
    { value: String(MatchFormat.ElevenAside), label: t('11-a-side') },
  ];

  const typeOptions = [
    { value: String(MatchType.Outdoor), label: t('Outdoor') },
    { value: String(MatchType.Indoor), label: t('Indoor') },
  ];

  // --- Preview calculations ---
  const validTeams = useMemo(() => teamNames.filter(n => n.trim() !== ''), [teamNames]);
  const bracketSize = useMemo(() => {
    let size = 1;
    while (size < validTeams.length) size *= 2;
    return validTeams.length >= 3 ? size : 0;
  }, [validTeams.length]);
  const totalRounds = bracketSize > 0 ? Math.log2(bracketSize) : 0;
  const totalMatches = bracketSize > 0 ? bracketSize - 1 : 0;
  const byeCount = bracketSize - validTeams.length;

  const handleAddTeam = () => {
    if (teamNames.length < 64) {
      setTeamNames([...teamNames, '']);
    }
  };

  const handleRemoveTeam = (index: number) => {
    if (teamNames.length > 3) {
      setTeamNames(teamNames.filter((_, i) => i !== index));
    }
  };

  const handleTeamNameChange = (index: number, value: string) => {
    const updated = [...teamNames];
    updated[index] = value;
    setTeamNames(updated);
    setApiError(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError(null);

    const filtered = teamNames.filter(n => n.trim() !== '');
    if (filtered.length < 3) {
      setApiError(t('At least 3 team names are required'));
      return;
    }
    if (!defaultLocation.trim()) {
      setApiError(t('Default location is required'));
      return;
    }
    if (!firstRoundDateTime) {
      setApiError(t('First round date is required'));
      return;
    }

    setIsSubmitting(true);

    try {
      const input: GenerateBracketInput = {
        teamNames: filtered.map(n => n.trim()),
        defaultLocation: defaultLocation.trim(),
        firstRoundDateTime: firstRoundDateTime,
        hoursBetweenMatches,
        daysBetweenRounds,
        format: parseInt(format) as MatchFormat,
        type: parseInt(type) as MatchType,
        allowRegenerate: true,
      };

      const result = await generateBracket(tournamentId, input);

      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to generate bracket'));
        return;
      }

      // Reset form
      setTeamNames(['', '', '']);
      setDefaultLocation('');
      setFirstRoundDateTime('');
      setHoursBetweenMatches(2);
      setDaysBetweenRounds(7);
      setFormat(String(MatchFormat.ElevenAside));
      setType(String(MatchType.Outdoor));

      onGenerated?.();
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setApiError(msg);
      console.error('Error generating bracket:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="lg" data-testid="generate-bracket-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Icon name="GitBranch" size={24} className="me-2 text-primary" />
            {t('generateBracket')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-6">
            {/* API Error */}
            {apiError && (
              <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Icon name="AlertCircle" size={18} className="text-red-600 me-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-800 dark:text-red-300">{apiError}</p>
                </div>
              </div>
            )}

            {/* Team Names */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">{t('teamNames')}</h3>
                <span className="text-xs text-muted-foreground">{validTeams.length} / 64</span>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto pe-1">
                {teamNames.map((name, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-6 text-end flex-shrink-0">{index + 1}.</span>
                    <Input
                      value={name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTeamNameChange(index, e.target.value)}
                      placeholder={`${t('Team')} ${index + 1}`}
                      className="flex-1"
                      disabled={isSubmitting}
                      data-testid={`bracket-team-name-${index}`}
                    />
                    {teamNames.length > 3 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTeam(index)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        aria-label={t('removeTeam')}
                        title={t('removeTeam')}
                        disabled={isSubmitting}
                        data-testid={`bracket-remove-team-${index}`}
                      >
                        <Icon name="X" size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {teamNames.length < 64 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddTeam}
                  disabled={isSubmitting}
                  data-testid="bracket-add-team-btn"
                >
                  <Icon name="Plus" size={14} className="me-1" />
                  {t('addTeam')}
                </Button>
              )}
            </div>

            {/* Match Settings */}
            <div className="space-y-4">
              <Input
                label={t('defaultLocation')}
                value={defaultLocation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDefaultLocation(e.target.value);
                  setApiError(null);
                }}
                placeholder={t('e.g., Dubai Sports City')}
                required
                disabled={isSubmitting}
                data-testid="bracket-default-location"
              />

              <DateTimeField
                label={t('firstRoundDate')}
                value={firstRoundDateTime}
                onChange={(v) => {
                  setFirstRoundDateTime(v);
                  setApiError(null);
                }}
                required
                disabled={isSubmitting}
                data-testid="bracket-first-round-datetime"
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('hoursBetweenMatches')}
                  type="number"
                  value={String(hoursBetweenMatches)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHoursBetweenMatches(Math.max(1, Math.min(24, parseInt(e.target.value) || 1)))}
                  min="1"
                  max="24"
                  disabled={isSubmitting}
                  data-testid="bracket-hours-between"
                />
                <Input
                  label={t('daysBetweenRounds')}
                  type="number"
                  value={String(daysBetweenRounds)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDaysBetweenRounds(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
                  min="1"
                  max="30"
                  disabled={isSubmitting}
                  data-testid="bracket-days-between"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Select
                  label={t('Match Format')}
                  value={format}
                  onChange={(value: string) => setFormat(value)}
                  options={formatOptions}
                  disabled={isSubmitting}
                  data-testid="bracket-format-select"
                />
                <Select
                  label={t('Match Type')}
                  value={type}
                  onChange={(value: string) => setType(value)}
                  options={typeOptions}
                  disabled={isSubmitting}
                  data-testid="bracket-type-select"
                />
              </div>
            </div>

            {/* Preview Section */}
            {validTeams.length >= 3 && (
              <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Icon name="Eye" size={14} className="text-primary" />
                  {t('bracketPreview')}
                </h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{totalRounds}</p>
                    <p className="text-xs text-muted-foreground">{t('roundCount')}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{totalMatches}</p>
                    <p className="text-xs text-muted-foreground">{t('Matches')}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{byeCount}</p>
                    <p className="text-xs text-muted-foreground">{t('byeCount')}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              data-testid="bracket-cancel-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={validTeams.length < 3}
              data-testid="bracket-generate-btn"
            >
              {isSubmitting ? t('generating') : t('generateBracket')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateBracketModal;
